import {
  Bell,
  Building2,
  ClipboardList,
  FileSpreadsheet,
  GraduationCap,
  LayoutDashboard,
  Settings,
  UserCheck,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import type { UserRole } from '../../types';

export interface NavigationPermission {
  resource: string;
  action: string;
}

export interface NavigationItem {
  icon: typeof LayoutDashboard;
  label: string;
  path: string;
  permissions: NavigationPermission[];
  mobilePriority?: boolean;
  // Less frequent pages go under the "Quản trị" menu in the desktop top bar.
  adminGroup?: boolean;
  hiddenForRoles?: UserRole[];
  // When set, only these roles see the item, in addition to the permission check.
  visibleForRoles?: UserRole[];
}

export const navigationItems: NavigationItem[] = [
  {
    icon: LayoutDashboard,
    label: 'Tổng quan',
    path: '/dashboard',
    mobilePriority: true,
    permissions: [
      { resource: 'dashboard', action: 'view_general' },
      { resource: 'dashboard', action: 'view' },
    ],
  },
  {
    icon: UserCheck,
    label: 'Buổi học',
    path: '/attendance',
    mobilePriority: true,
    permissions: [
      { resource: 'attendance', action: 'read_all' },
      { resource: 'attendance', action: 'read_own' },
      { resource: 'attendance', action: 'read' },
    ],
  },
  {
    icon: Users,
    label: 'Học sinh',
    path: '/students',
    mobilePriority: true,
    permissions: [
      { resource: 'student', action: 'read_all' },
      { resource: 'student', action: 'read_own_classes' },
      { resource: 'student', action: 'read' },
    ],
  },
  {
    icon: GraduationCap,
    label: 'Lớp học',
    path: '/classes',
    mobilePriority: true,
    permissions: [
      { resource: 'class', action: 'read_all' },
      { resource: 'class', action: 'read_own' },
      { resource: 'class', action: 'read' },
    ],
  },
  {
    icon: Building2,
    label: 'Cơ sở',
    path: '/campuses',
    adminGroup: true,
    hiddenForRoles: ['TEACHER', 'ASSISTANT'],
    permissions: [
      { resource: 'campus', action: 'read_all' },
      { resource: 'campus', action: 'read_own' },
      { resource: 'campus', action: 'read' },
    ],
  },
  {
    icon: UserCog,
    label: 'Giáo viên',
    path: '/teachers',
    permissions: [
      { resource: 'teacher', action: 'read_all' },
      { resource: 'teacher', action: 'read' },
    ],
  },
  {
    icon: Bell,
    label: 'Thông báo',
    path: '/notifications',
    adminGroup: true,
    visibleForRoles: ['ADMIN', 'MANAGER'],
    // Same as the bell; teachers and assistants reach the page from the bell.
    permissions: [
      { resource: 'homework_alert', action: 'read' },
      { resource: 'attendance', action: 'read_all' },
      { resource: 'attendance', action: 'read_own' },
      { resource: 'manual_alert', action: 'read' },
    ],
  },
  {
    icon: ClipboardList,
    label: 'Nhật ký nhân sự',
    path: '/staff-activity',
    adminGroup: true,
    visibleForRoles: ['ADMIN', 'MANAGER'],
    permissions: [
      { resource: 'attendance', action: 'read_all' },
      { resource: 'attendance', action: 'read_own' },
    ],
  },
  {
    icon: Wallet,
    label: 'Chấm công',
    path: '/payroll',
    visibleForRoles: ['ADMIN'],
    permissions: [
      { resource: 'teacher', action: 'read_all' },
      { resource: 'user', action: 'read_all' },
    ],
  },
  {
    icon: FileSpreadsheet,
    label: 'Dữ liệu & báo cáo',
    path: '/reports',
    adminGroup: true,
    // Teachers and assistants export the classes they teach; the server decides what each person sees.
    visibleForRoles: ['ADMIN', 'MANAGER', 'TEACHER', 'ASSISTANT'],
    permissions: [
      { resource: 'attendance', action: 'read_all' },
      { resource: 'attendance', action: 'read_own' },
      { resource: 'class', action: 'read_all' },
      { resource: 'class', action: 'read_own' },
    ],
  },
  {
    icon: Settings,
    label: 'Tài khoản hệ thống',
    path: '/users',
    adminGroup: true,
    hiddenForRoles: ['TEACHER', 'ASSISTANT'],
    permissions: [
      { resource: 'user', action: 'read_all' },
      { resource: 'user', action: 'read' },
    ],
  },
];

export function getVisibleNavigationItems(
  hasAnyPermission: (permissions: NavigationPermission[]) => boolean,
  role?: UserRole,
) {
  return navigationItems.filter((item) => {
    if (role && item.hiddenForRoles?.includes(role)) return false;
    if (item.visibleForRoles && (!role || !item.visibleForRoles.includes(role))) return false;
    return hasAnyPermission(item.permissions);
  });
}

export function splitMobileNavigation(items: NavigationItem[]) {
  return {
    primary: items.filter((item) => item.mobilePriority).slice(0, 4),
    secondary: items.filter((item) => !item.mobilePriority),
  };
}

export function splitDesktopNavigation(items: NavigationItem[]) {
  return {
    primary: items.filter((item) => !item.adminGroup),
    admin: items.filter((item) => item.adminGroup),
  };
}
